import React, { useEffect, useRef, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import { useSoftUIController } from "hooks/context";

const GroupMap = ({ localMap, isMapLoaded, groups = [], sites = [], devices = [], hoveredGroupId, setHoveredGroupId, user }) => {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg } = controller;

    const hasFittedBounds = useRef(false);
    const stateRef = useRef({ setHoveredGroupId });

    useEffect(() => {
        stateRef.current.setHoveredGroupId = setHoveredGroupId;
    }, [setHoveredGroupId]);

    const groupPolygonsGeoJSON = useMemo(() => {
        const features = [];
        groups.forEach(group => {
            const groupId = group._id || group.group_id;
            
            if (group.geojson && group.geojson.length > 0) {
                group.geojson.forEach(geo => {
                    features.push({
                        type: 'Feature', geometry: geo.geometry || geo, 
                        properties: { groupId: groupId, name: group.name, featureColor: group.color || '#17c1e8' }
                    });
                });
            }
            else if (group.type === 'monitoring' && group.devices && group.devices.length > 0) {
                const groupDevices = devices.filter(d => group.devices.includes(d.properties.uuid));
                const coords = groupDevices.map(d => d.geometry?.coordinates).filter(Boolean);
                
                if (coords.length > 0) {
                    let baseGeo;
                    if (coords.length === 1) baseGeo = turf.point(coords[0]);
                    else if (coords.length === 2) baseGeo = turf.lineString(coords);
                    else {
                        baseGeo = turf.convex(turf.points(coords));
                        if (!baseGeo) baseGeo = turf.lineString(coords);
                    }

                    const bbox = turf.bbox(baseGeo);
                    const swCorner = turf.point([bbox[0], bbox[1]]);
                    const neCorner = turf.point([bbox[2], bbox[3]]);
                    const diagonalDistance = turf.distance(swCorner, neCorner, { units: 'kilometers' });
                    const dynamicPadding = Math.max(0.1, diagonalDistance * 0.01);

                    const paddedPolygon = turf.buffer(baseGeo, (dynamicPadding > 10 ? 10 : dynamicPadding), { units: 'kilometers' });
                    
                    if (paddedPolygon) {
                        features.push({
                            type: 'Feature', geometry: paddedPolygon.geometry || paddedPolygon, 
                            properties: { groupId: groupId, name: group.name, featureColor: group.color || '#17c1e8' }
                        });
                    }
                }
            } 
        });
        return { type: 'FeatureCollection', features };
    }, [groups, devices]);

    const latestGeoJSON = useRef(groupPolygonsGeoJSON);
    useEffect(() => {
        latestGeoJSON.current = groupPolygonsGeoJSON;
    }, [groupPolygonsGeoJSON]);

    useEffect(() => {
        if (!isMapLoaded || !localMap.current) return;
        const mapInstance = localMap.current;
        let isMounted = true;

        const setupLayers = () => {
            if (!isMounted) return;

            if (!mapInstance.getSource('groups-source')) {
                mapInstance.addSource('groups-source', { type: 'geojson', data: latestGeoJSON.current });
                mapInstance.addLayer({ id: 'groups-fill', type: 'fill', source: 'groups-source', paint: { 'fill-color': '#cccccc', 'fill-opacity': 0.3 } });
                mapInstance.addLayer({ id: 'groups-outline', type: 'line', source: 'groups-source', paint: { 'line-color': '#999999', 'line-width': 2 } });
                
                hasFittedBounds.current = false; 
            }
            if (!mapInstance.getSource('hovered-sites-source')) {
                mapInstance.addSource('hovered-sites-source', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
                mapInstance.addLayer({ id: 'hovered-sites-layer', type: 'circle', source: 'hovered-sites-source', paint: { 'circle-color': ['get', 'featureColor'], 'circle-radius': 7, 'circle-stroke-width': 2, 'circle-stroke-color': '#ffffff' } });
            }
        };

        if (mapInstance.isStyleLoaded()) {
            setupLayers();
        } else {
            mapInstance.once('style.load', setupLayers);
        }

        const onMouseMove = (e) => {
            if (e.features.length > 0) {
                mapInstance.getCanvas().style.cursor = 'pointer';
                stateRef.current.setHoveredGroupId(e.features[0].properties.groupId);
            }
        };
        const onMouseLeave = () => {
            mapInstance.getCanvas().style.cursor = '';
            stateRef.current.setHoveredGroupId(null);
        };

        mapInstance.on('mousemove', 'groups-fill', onMouseMove);
        mapInstance.on('mouseleave', 'groups-fill', onMouseLeave);

        return () => {
            isMounted = false;
            mapInstance.off('mousemove', 'groups-fill', onMouseMove);
            mapInstance.off('mouseleave', 'groups-fill', onMouseLeave);

            if (mapInstance.getStyle()) {
                ['groups-fill', 'groups-outline', 'hovered-sites-layer'].forEach(l => {
                    if (mapInstance.getLayer(l)) mapInstance.removeLayer(l);
                });
                ['groups-source', 'hovered-sites-source'].forEach(s => {
                    if (mapInstance.getSource(s)) mapInstance.removeSource(s);
                });
            }
        };
    }, [isMapLoaded, localMap]); 

    useEffect(() => {
        if (!isMapLoaded || !localMap.current) return;
        const source = localMap.current.getSource('groups-source');
        if (source) {
            source.setData(groupPolygonsGeoJSON);
            hasFittedBounds.current = false;
        }
    }, [isMapLoaded, localMap, groupPolygonsGeoJSON]);

    useEffect(() => {
        if (!isMapLoaded || !localMap.current) return;
        const mapInstance = localMap.current;
        
        if (!mapInstance.getLayer('groups-fill')) return;

        const defaultFillColor = '#cccccc';
        const defaultLineColor = '#999999';

        mapInstance.setPaintProperty('groups-fill', 'fill-color', ['case', ['==', ['get', 'groupId'], hoveredGroupId || ''], ['get', 'featureColor'], defaultFillColor]);
        mapInstance.setPaintProperty('groups-fill', 'fill-opacity', ['case', ['==', ['get', 'groupId'], hoveredGroupId || ''], 0.5, 0.3]);
        mapInstance.setPaintProperty('groups-outline', 'line-color', ['case', ['==', ['get', 'groupId'], hoveredGroupId || ''], ['get', 'featureColor'], defaultLineColor]);

        let activePoints = [];
        var activeGroup = groups.find(g => (g._id === hoveredGroupId || g.group_id === hoveredGroupId)) || null;
        
        if (hoveredGroupId && activeGroup) {
            if (activeGroup.type === 'monitoring' && activeGroup.devices) {
                const device_uuids = activeGroup.devices.map(d => d.uuid || d);
                activePoints = devices.filter(d => device_uuids.includes(d.properties.uuid));
            } 
            else if (activeGroup.sites) {
                const site_ids = activeGroup.sites.map(s => s.site_id || s);
                activePoints = sites.filter(s => site_ids.includes(s.properties.site_id));
            }
        }

        const sitesSource = mapInstance.getSource('hovered-sites-source');
        if (sitesSource) {
            sitesSource.setData({ 
                type: 'FeatureCollection', 
                features: activePoints.map(aP => { return { type: 'Feature', geometry: aP.geometry, properties: { ...aP, featureColor: activeGroup?.color || '#17c1e8'}}}) 
            });
        }
    }, [hoveredGroupId, isMapLoaded, localMap, groups, sites, devices]);

    useEffect(() => {
        if (!isMapLoaded || !localMap.current || hasFittedBounds.current) return;
        
        if (groupPolygonsGeoJSON.features.length === 0) {
            hasFittedBounds.current = true;
            const orgLocation = user?.organisations?.find(o => o.organisation_id === selectedOrg)?.location || { lat: 0, lng: 0 };
            
            localMap.current.stop();
            setTimeout(() => {
                if (localMap.current) {
                    localMap.current.resize();
                    localMap.current.flyTo({ center: Object.values(orgLocation).reverse(), zoom: 10, speed: 1.5 });
                }
            }, 50);
            return;
        }
        
        const bbox = turf.bbox(groupPolygonsGeoJSON);
        if (!bbox || bbox.some(val => !isFinite(val))) return;

        const bounds = new mapboxgl.LngLatBounds([bbox[0], bbox[1]], [bbox[2], bbox[3]]);
        const set_padding = 150;
        const camOpts = {
            padding: deviceLayout === 'mobile' ? { top: set_padding / 2, left: set_padding / 2, right: set_padding / 2, bottom: set_padding / 2 } : set_padding,
            maxZoom: 12,
            duration: 1200
        };
        
        hasFittedBounds.current = true;
        localMap.current.stop();
        setTimeout(() => {
            if (localMap.current) {
                localMap.current.resize();
                localMap.current.fitBounds(bounds, camOpts);
            }
        }, 50);
    }, [isMapLoaded, localMap, groupPolygonsGeoJSON, deviceLayout, selectedOrg, user]);

    return null;
};

export default GroupMap;